import { getGraphQLEndpoint } from '../apolloClient';
import { currentIdToken } from './freshIdToken';
import { MULTIPART_THRESHOLD_BYTES } from './multipartUpload';
import { graphqlTransport, uploadAssetInParts } from './multipartUploadApi';

export type AssetUploadKind =
  | 'original-video'
  // A multicam set's separate sound recording (library storage, like a video).
  | 'original-audio'
  | 'reference-video'
  | 'image'
  | 'audio'
  | 'document'
  | 'profile-image';

interface UploadHeader {
  name: string;
  value: string;
}

interface DirectUploadIntent {
  uploadUrl: string;
  objectKey: string;
  method: string;
  headers: UploadHeader[];
  expiresAt: string;
}

export interface StoredAsset {
  url: string;
  objectKey: string;
  storageProvider: 'r2';
  filename: string;
  contentType: string;
  sizeBytes: number;
}

async function postGraphQL<T>(query: string, variables: Record<string, unknown>, token: string): Promise<T> {
  const response = await fetch(getGraphQLEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query, variables }),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok || body?.errors?.length) {
    const message = body?.errors?.map((error: any) => error?.message).filter(Boolean).join('; ');
    throw new Error(message || "We couldn't start the upload. Try again in a moment.");
  }
  if (!body?.data) throw new Error("We couldn't start the upload. Try again in a moment.");
  return body.data as T;
}

function putFile(
  intent: DirectUploadIntent,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    // Cancelled while the upload was being authorized: aborting an XHR that
    // was never sent fires no event, so reject here or the promise would
    // never settle.
    if (signal?.aborted) {
      reject(new DOMException('Upload cancelled', 'AbortError'));
      return;
    }
    const request = new XMLHttpRequest();
    const handleAbort = () => request.abort();
    request.open(intent.method, intent.uploadUrl);
    intent.headers.forEach(({ name, value }) => request.setRequestHeader(name, value));
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(Math.min(99, Math.round((event.loaded / event.total) * 100)));
      }
    };
    request.onerror = () => reject(new Error('The upload was interrupted. Check your connection and try again.'));
    request.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    request.onload = () => {
      signal?.removeEventListener('abort', handleAbort);
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new Error(describeDirectUploadFailure(request.status)));
    };
    signal?.addEventListener('abort', handleAbort, { once: true });
    request.send(file);
  });
}

export interface AssetUploadOptions {
  /** Told when an earlier upload of the same file is being continued (large files only). */
  onResume?: (info: { uploadedBytes: number; totalBytes: number }) => void;
}

export async function uploadAssetToR2(
  file: File,
  kind: AssetUploadKind,
  token: string,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
  options: AssetUploadOptions = {},
): Promise<StoredAsset> {
  const contentType = file.type || 'application/octet-stream';
  // Large files go up in parts: retried part by part, and resumable after a
  // dropped connection or a reload by choosing the same file again.
  if (file.size >= MULTIPART_THRESHOLD_BYTES) {
    return uploadAssetInParts(file, kind, contentType, graphqlTransport(getGraphQLEndpoint(), () => currentIdToken(token)), {
      onProgress,
      signal,
      onResume: options.onResume,
    });
  }
  onProgress?.(0);
  // fileSizeBytes is a Float: GraphQL Int stops just under 2 GiB. The API
  // checks the plan's per-file limit here, before any bytes are sent.
  const created = await postGraphQL<{ createAssetUpload: DirectUploadIntent }>(
    `mutation CreateAssetUpload($kind: String!, $filename: String!, $contentType: String!, $fileSizeBytes: Float!) {
      createAssetUpload(kind: $kind, filename: $filename, contentType: $contentType, fileSizeBytes: $fileSizeBytes) {
        uploadUrl objectKey method expiresAt
        headers { name value }
      }
    }`,
    { kind, filename: file.name, contentType, fileSizeBytes: file.size },
    token,
  );
  const { objectKey } = created.createAssetUpload;
  try {
    await putFile(created.createAssetUpload, file, onProgress, signal);
  } catch (error) {
    // Stop holding storage for an upload that will never finish (cancelled or
    // failed), so the person can pick another file straight away. Best effort:
    // the reservation also lapses when the upload link expires.
    currentIdToken(token)
      .then((freshToken) =>
        postGraphQL(
          'mutation CancelAssetUpload($objectKey: String!) { cancelAssetUpload(objectKey: $objectKey) }',
          { objectKey },
          freshToken,
        ),
      )
      .catch(() => undefined);
    throw error;
  }
  // A large file can take longer than the one-hour token captured above.
  const freshToken = await currentIdToken(token);
  const completed = await postGraphQL<{ completeAssetUpload: StoredAsset }>(
    `mutation CompleteAssetUpload($kind: String!, $objectKey: String!, $filename: String!, $contentType: String!, $fileSizeBytes: Float!) {
      completeAssetUpload(kind: $kind, objectKey: $objectKey, filename: $filename, contentType: $contentType, fileSizeBytes: $fileSizeBytes) {
        url objectKey storageProvider filename contentType sizeBytes
      }
    }`,
    {
      kind,
      objectKey,
      filename: file.name,
      contentType,
      fileSizeBytes: file.size,
    },
    freshToken,
  );
  onProgress?.(100);
  return completed.completeAssetUpload;
}

/**
 * Turn a failed direct-to-storage PUT into something a person can act on.
 * A bare "failed (403)" gave no hint that the signed link had simply run out
 * of time on a slow connection.
 */
export function describeDirectUploadFailure(status: number): string {
  if (status === 403) {
    return 'The upload link expired before the file finished uploading. Please try again — a faster or wired connection helps with large files.';
  }
  if (status === 413) return 'This file is larger than the storage service accepts.';
  if (status === 0) return 'The connection dropped during the upload. Check your network and try again.';
  if (status >= 500) return 'The storage service had a problem. Please try again in a moment.';
  return "The upload didn't finish. Please try again.";
}
